import React, { useRef } from 'react';
import { CATEGORY_COLORS, formatPrice } from '../data/components';
import { Button } from '../components/ui/button';
import { X, Printer, Download } from 'lucide-react';

const AA_LOGO = '/aa-logo.png';
const AA_INFO = {
  name: 'Archana Automotives',
  tagline: 'The new age automotive company',
  office: 'W115A, First Floor, Third Avenue, Anna Nagar, Chennai - 600040',
  factory: 'SY No. 272/3A, Sriperumpudur - Chengalpet Road, Vadakal Village, Sriperumpudur, Kancheepuram District, Chennai - 600166',
  phone: '+91 96772 66377, +91 87544 65423',
  email: 'info@archanaautomotives.com',
  website: 'www.archanaautomotives.com',
};

export default function QuoteGenerator({ components, placedComponents, totalCost, totalWeight, designName, user, onClose }) {
  const printRef = useRef(null);

  const placedWithDetails = placedComponents.map(pc => {
    const comp = components.find(c => c.comp_id === pc.comp_id);
    return { ...pc, comp };
  }).filter(pc => pc.comp);

  // Group by category
  const grouped = {};
  placedWithDetails.forEach(pc => {
    const cat = pc.comp.category;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(pc);
  });

  const today = new Date();
  const quoteNum = `AA-${today.getFullYear()}${String(today.getMonth()+1).padStart(2,'0')}${String(today.getDate()).padStart(2,'0')}-${Math.random().toString(36).substr(2,5).toUpperCase()}`;
  const validUntil = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

  const gst = Math.round(totalCost * 0.18);
  const grandTotal = totalCost + gst;

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Quote - ${quoteNum}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&family=Chivo:wght@700;900&family=JetBrains+Mono:wght@400;500&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
        body { font-family: 'Manrope', sans-serif; color: #1e293b; padding: 40px; background: white; }
        .quote-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 3px solid #1e293b; }
        .logo-area img { height: 60px; margin-bottom: 8px; }
        .logo-area h1 { font-family: 'Chivo', sans-serif; font-size: 20px; color: #1e293b; margin: 0; }
        .logo-area p { font-size: 11px; color: #64748b; margin: 0; }
        .quote-meta { text-align: right; }
        .quote-meta h2 { font-family: 'Chivo', sans-serif; font-size: 28px; color: #3b82f6; margin-bottom: 8px; }
        .quote-meta p { font-size: 11px; color: #64748b; line-height: 1.8; margin: 0; }
        .quote-meta strong { color: #1e293b; }
        .section-title { font-family: 'Chivo', sans-serif; font-size: 14px; color: #3b82f6; text-transform: uppercase; letter-spacing: 0.1em; margin: 24px 0 12px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 24px; }
        .info-box { padding: 16px; background-color: #f8fafc !important; border-radius: 8px; border: 1px solid #e2e8f0; }
        .info-box h4 { font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: #94a3b8; margin-bottom: 8px; }
        .info-box p { font-size: 12px; color: #334155; line-height: 1.6; margin: 0; }
        table { width: 100%; border-collapse: collapse; margin: 16px 0; }
        th { background-color: #1e293b !important; color: white !important; padding: 10px 12px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; text-align: left; }
        td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 12px; color: #334155; }
        tr:nth-child(even) td { background-color: #f8fafc !important; }
        .cat-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; }
        .totals { margin-top: 16px; padding: 20px; background-color: #f0f9ff !important; border-radius: 8px; border: 1px solid #bae6fd; }
        .total-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; color: #334155; }
        .total-row.grand { font-size: 18px; font-weight: 700; color: #1e293b; border-top: 2px solid #3b82f6; padding-top: 12px; margin-top: 8px; font-family: 'Chivo', sans-serif; }
        .terms { margin-top: 32px; padding-top: 20px; border-top: 1px solid #e2e8f0; }
        .terms h4 { font-size: 12px; font-weight: 600; color: #1e293b; margin-bottom: 8px; }
        .terms li { font-size: 10px; color: #64748b; line-height: 1.8; }
        .footer { margin-top: 40px; text-align: center; padding-top: 20px; border-top: 2px solid #1e293b; }
        .footer p { font-size: 10px; color: #64748b; line-height: 1.8; margin: 0; }
        @media print { 
          body { padding: 20px; } 
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      </style>
      </head><body>${content.innerHTML}</body></html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" data-testid="quote-overlay">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="quote-dialog">
        {/* Toolbar */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between rounded-t-2xl">
          <h2 className="font-chivo font-bold text-slate-900 text-lg">Design Quotation</h2>
          <div className="flex items-center gap-2">
            <Button onClick={handlePrint} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5" data-testid="print-quote-btn">
              <Printer className="w-3.5 h-3.5" /> Print / PDF
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-quote-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Quote Content */}
        <div ref={printRef} className="px-8 py-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-8 pb-6 border-b-[3px] border-slate-800">
            <div>
              <img src={AA_LOGO} alt="Archana Automotives" className="h-14 mb-2" />
              <h1 className="font-chivo font-bold text-xl text-slate-900">{AA_INFO.name}</h1>
              <p className="text-[11px] text-slate-500">{AA_INFO.tagline}</p>
            </div>
            <div className="text-right">
              <h2 className="font-chivo font-bold text-2xl text-blue-600 mb-2">QUOTATION</h2>
              <p className="text-[11px] text-slate-500 leading-loose">
                <strong className="text-slate-800">Quote #:</strong> {quoteNum}<br />
                <strong className="text-slate-800">Date:</strong> {today.toLocaleDateString('en-IN')}<br />
                <strong className="text-slate-800">Valid Until:</strong> {validUntil.toLocaleDateString('en-IN')}<br />
                <strong className="text-slate-800">Design:</strong> {designName}
              </p>
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="text-[10px] uppercase tracking-[0.1em] text-slate-400 mb-2">From</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                <strong className="text-slate-800">{AA_INFO.name}</strong><br />
                Office: {AA_INFO.office}<br />
                Ph: {AA_INFO.phone}<br />
                {AA_INFO.email}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="text-[10px] uppercase tracking-[0.1em] text-slate-400 mb-2">Customer</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                <strong className="text-slate-800">{user?.name || 'Customer'}</strong><br />
                {user?.email || ''}<br />
                Vehicle: Force Motors Urbania
              </p>
            </div>
          </div>

          {/* Component Table */}
          <p className="font-chivo text-sm text-blue-600 uppercase tracking-[0.1em] mb-3">Interior Components</p>
          <table className="w-full border-collapse mb-4">
            <thead>
              <tr>
                <th className="bg-slate-800 text-white px-3 py-2.5 text-[10px] uppercase tracking-[0.1em] text-left rounded-tl-lg">#</th>
                <th className="bg-slate-800 text-white px-3 py-2.5 text-[10px] uppercase tracking-[0.1em] text-left">Component</th>
                <th className="bg-slate-800 text-white px-3 py-2.5 text-[10px] uppercase tracking-[0.1em] text-left">Category</th>
                <th className="bg-slate-800 text-white px-3 py-2.5 text-[10px] uppercase tracking-[0.1em] text-left">Size</th>
                <th className="bg-slate-800 text-white px-3 py-2.5 text-[10px] uppercase tracking-[0.1em] text-right">Weight</th>
                <th className="bg-slate-800 text-white px-3 py-2.5 text-[10px] uppercase tracking-[0.1em] text-right rounded-tr-lg">Price</th>
              </tr>
            </thead>
            <tbody>
              {placedWithDetails.map((pc, i) => (
                <tr key={pc.instance_id} className={i % 2 === 1 ? 'bg-slate-50' : ''}>
                  <td className="px-3 py-2.5 text-xs text-slate-400 border-b border-slate-100">{i + 1}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-700 font-medium border-b border-slate-100">{pc.comp.name}</td>
                  <td className="px-3 py-2.5 border-b border-slate-100">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: `${CATEGORY_COLORS[pc.comp.category]?.fill}20`, color: CATEGORY_COLORS[pc.comp.category]?.fill }}>
                      {pc.comp.category}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-500 border-b border-slate-100 font-mono">{pc.comp.grid_w}x{pc.comp.grid_h}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500 border-b border-slate-100 text-right font-mono">{pc.comp.weight}kg</td>
                  <td className="px-3 py-2.5 text-xs text-slate-800 border-b border-slate-100 text-right font-semibold font-mono">{formatPrice(pc.comp.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="p-5 bg-blue-50 rounded-xl border border-blue-200 mb-6">
            <div className="flex justify-between py-1.5 text-sm text-slate-600">
              <span>Subtotal ({placedComponents.length} items)</span>
              <span className="font-mono">{formatPrice(totalCost)}</span>
            </div>
            <div className="flex justify-between py-1.5 text-sm text-slate-600">
              <span>Total Weight</span>
              <span className="font-mono">{totalWeight}kg</span>
            </div>
            <div className="flex justify-between py-1.5 text-sm text-slate-600">
              <span>GST (18%)</span>
              <span className="font-mono">{formatPrice(gst)}</span>
            </div>
            <div className="flex justify-between pt-3 mt-2 border-t-2 border-blue-400 text-lg font-bold text-slate-900 font-chivo">
              <span>Grand Total</span>
              <span className="text-blue-600">{formatPrice(grandTotal)}</span>
            </div>
          </div>

          {/* Terms */}
          <div className="pt-5 border-t border-slate-200 mb-6">
            <h4 className="text-xs font-semibold text-slate-800 mb-2">Terms & Conditions</h4>
            <ul className="list-disc pl-4 space-y-1">
              <li className="text-[10px] text-slate-500 leading-relaxed">This quotation is valid for 30 days from the date of issue.</li>
              <li className="text-[10px] text-slate-500 leading-relaxed">50% advance payment required to confirm the order.</li>
              <li className="text-[10px] text-slate-500 leading-relaxed">Estimated delivery: 15-20 working days after confirmation.</li>
              <li className="text-[10px] text-slate-500 leading-relaxed">Prices are subject to change based on material availability.</li>
              <li className="text-[10px] text-slate-500 leading-relaxed">1-year warranty on all interior components and installation.</li>
              <li className="text-[10px] text-slate-500 leading-relaxed">Installation charges included in the quoted price.</li>
            </ul>
          </div>

          {/* Footer */}
          <div className="text-center pt-5 border-t-2 border-slate-800">
            <p className="text-[10px] text-slate-500 leading-loose">
              <strong className="text-slate-700">{AA_INFO.name}</strong><br />
              Office: {AA_INFO.office}<br />
              Factory: {AA_INFO.factory}<br />
              Ph: {AA_INFO.phone} | {AA_INFO.email} | {AA_INFO.website}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
